import { FC } from "react";

import { useIntl } from "react-intl";

import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import { useHash } from "@/lib/hooks/use-hash";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import {
  useListingDataForNormalization,
  normalizeListingObj,
} from "src/app/analytics/normalize-listing";
import { useGalleryHashModals } from "~/components/gallery/useGalleryHashModals";
import { useListingData } from "~/context/ListingPageDataContext";
import { IVideoEmbedData } from "~/types/api-types";
import { ListingImageCondensed } from "~/types/listing.types";

import { Header } from "./header/header";
import { useMediaProcessing, useSetListingMediaFromHash } from "./hooks";
import { useHashMediaType } from "./photo-gallery/hooks/use-hash-media";
import { useMediaFilter } from "./photo-gallery/hooks/use-hash-media-filter";
import { PhotoGallery } from "./photo-gallery/PhotoGallery";
import { HeroSectionProps } from "./types";
import {
  handlePhotoCarouselInteraction,
  createSharedListingProps,
  defaultListingAltText,
} from "./utils";

export const HeroSection: FC<HeroSectionProps> = ({
  title,
  listingCity,
  listingState,
  listingCountry,
  cityGoogle,
  stateGoogle,
  neighborhood,
  neighborhoodBreadcrumb,
  useTypeBreadcrumb,
  listingId,
  fbShare,
  twitterShare,
  emailShare,
  useType,
  metro,
  category,
  listingVenueName,
  listingAwards,
  toggleFavorite,
  isFav,
}) => {
  const intl = useIntl();
  const { hashMap, updateHash } = useHash();
  const isMDScreen = useMediaQuery(`(min-width: ${Breakpoints.MD}px)`);
  const { data } = useListingData();
  const { videos = [], images = [] } =
    data ||
    ({} as {
      videos: IVideoEmbedData[];
      images: ListingImageCondensed[];
    });
  const listingData = useListingDataForNormalization();
  const normalizedListingData = normalizeListingObj(listingData);

  const defaultAlt = defaultListingAltText({
    title,
    city: listingCity,
    state: listingState,
    neighborhood,
    intl,
  });

  // Custom hooks for business logic
  const { formattedImages, combinedMedia } = useMediaProcessing({
    images,
    videos,
    defaultAlt,
  });

  // Create shared props to reduce prop drilling
  const sharedProps = createSharedListingProps({
    listingId,
    useType,
    metro,
    category,
    fbShare,
    twitterShare,
    emailShare,
    toggleFavorite,
    isFav,
    isMDScreen,
  });

  const { activeMediaType, handleMediaTypeChange } = useHashMediaType();
  const { filteredMedia } = useMediaFilter({
    media: combinedMedia,
    activeMediaType,
  });

  const { setStartingIndex } = useGalleryHashModals({
    gallery: {
      activeMediaType,
      analyticData: normalizedListingData,
      handleMediaTypeChange,
      media: filteredMedia,
      originalMedia: combinedMedia,
      title,
      ...sharedProps,
    },
    getImageId: (index) =>
      combinedMedia[index]?.external_id ?? combinedMedia[index]?.id,
    photoViewer: {
      images: formattedImages,
      media: filteredMedia,
      ...sharedProps,
    },
  });

  useSetListingMediaFromHash({
    combinedMedia,
    hashMap,
    onImageIdFound: setStartingIndex,
  });

  const handleOnClick = () => {
    handlePhotoCarouselInteraction(listingId, "listing gallery");
    updateHash("#type=media");
  };

  return (
    <div className="tw-flex tw-flex-col-reverse sm:tw-flex-col">
      <div className="tw-mt-24 sm:tw-mt-0 ps-container-lg-margin ps-container-fixed">
        <div className="ps-row">
          <Header
            title={title}
            state={listingState}
            cityGoogle={cityGoogle}
            stateGoogle={stateGoogle}
            neighborhood={neighborhood}
            neighborhoodBreadcrumb={neighborhoodBreadcrumb}
            useTypeBreadcrumb={useTypeBreadcrumb}
            listingVenueName={listingVenueName}
            {...sharedProps}
          />
        </div>
      </div>
      <div className="ps-container-lg-margin ps-container-fixed xs:!tw-px-0 sm:!tw-px-[30px] xl:!tw-px-[144px]">
        <div className="ps-row">
          <div className="col-xs-12 xs:!tw-px-0 sm:!tw-px-[10px] xl:!tw-px-16">
            <PhotoGallery
              images={formattedImages}
              onImgSelected={handleOnClick}
              listingAwards={listingAwards}
              listingCity={listingCity}
              listingCountry={listingCountry}
              analyticData={normalizedListingData}
              {...sharedProps}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
